import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AuthService } from '../../services/auth/auth.service';
import { EventBus } from '../../services/auth/event.bus';
import { REQUEST_TYPE_HEADER_KEY } from '../../services/auth/base-api.service';
import { ErrorHelper } from '../helpers/error.helper';
import { EventTypeEnum, RequestTypeEnum, ApiNamesEnum } from '../enums';
import { environment } from 'src/environments/environment';
import { ExtractedError } from 'src/app/shared/models/ExtractedError';
import { CerberusErrorCodesEnum } from '../enums/error-codes';
import { ErrorDetails } from 'src/app/shared/interfaces/error-details.interface';

@Injectable()
export class CustomInterceptor implements HttpInterceptor {
    constructor(private eventBus: EventBus, private authService: AuthService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const dediconUrls = ['https://api-educatief.dedicon.nl', 'https://acc-api-educatief.dedicon.nl'];
        const isDediconOrStiRequest = [...dediconUrls, environment.sti.baseUrl].findIndex(x => request.url.startsWith(x)) > -1;
        if ((!request.url.includes('api/') && !request.url.includes('connect/')) || isDediconOrStiRequest) {
            return next.handle(request);
        }

        const requestType = request.headers.get(REQUEST_TYPE_HEADER_KEY) as RequestTypeEnum;
        request.headers.delete(REQUEST_TYPE_HEADER_KEY);
        this.eventBus.emit(EventTypeEnum.HTTP_REQUEST, { requestType });

        const observable = new Observable<HttpEvent<any>>((observer: any) => {
            const onSuccess = (resp: HttpResponse<any>) => {
                if (!!resp.status) {
                    this.eventBus.emit(EventTypeEnum.HTTP_RESPONSE, { requestType });
                }
                observer.next(resp);
            };

            const onComplete = () => observer.complete();

            const onError = async (error: HttpErrorResponse) => {
                const errorDetails = await this.tryParseErrorBlob(error)

                if (errorDetails !== null) {
                    error = {...error, error: errorDetails};
                }

                const extractedError = this.extractErrorDetails(request, error);

                observer.error({
                    ...error,
                    errorCode: extractedError.errorCode,
                    apiName: extractedError.apiName
                });

                // ignore the file not found error from cerberus
                if (extractedError.errorCode === CerberusErrorCodesEnum.FileNotFound
                // ignore the errorCode 6 from cerberus, when you were kicked and now have an invalid token (and refresh token)
                    || extractedError.errorCode === CerberusErrorCodesEnum.Kicked
                ) {
                    return;
                }

                this.eventBus.emit(EventTypeEnum.HTTP_ERROR, {
                    response: error,
                    request,
                    requestType,
                    errorCode: extractedError.errorCode,
                    apiName: extractedError.apiName
                });
            };

            next.handle(this.prepare(request)).subscribe(onSuccess, async (error) => {
                try {
                    if (error.status === 401) {
                        let isRefreshed = false;
                        try {
                            await this.authService.refreshToken();
                            isRefreshed = true;
                        } catch { }

                        if (isRefreshed) {
                            next.handle(this.prepare(request)).subscribe(onSuccess, onError, onComplete);
                        } else {
                            this.authService.logout();
                            throw new Error('Couldn\'t refresh the token');
                        }
                    } else {
                        throw new Error('Unhandled exception');
                    }
                } catch (e) {
                    onError(error);
                }
            }, onComplete);
        });

        return observable;
    }

    private prepare(request: HttpRequest<any>): HttpRequest<any> {
        if (this.authService.isAuthenticated()) {
            const authInfo = this.authService.getAuthInfo();
            if (authInfo) {
                request = request.clone({
                    setHeaders: {
                        Authorization: `${authInfo.token_type} ${authInfo.access_token}`
                    }
                });
            }

        }

        return request;
    }

    private extractErrorDetails(request: HttpRequest<any>, error: HttpErrorResponse): ExtractedError {
        let apiName: ApiNamesEnum = ApiNamesEnum.Cerberus;
        const url = request.url.toLowerCase();

        if (url.includes(environment.connectedApiUrls.cerberus)) {
            apiName = ApiNamesEnum.Cerberus;
        } else if (url.includes(environment.connectedApiUrls.huginn) || url.includes(environment.connectedApiUrls.dictionaryApi)) {
            apiName = ApiNamesEnum.Huginn;
        } else if (url.includes(environment.connectedApiUrls.nephilim)) {
            apiName = ApiNamesEnum.Nephilim;
        } else if (url.includes(environment.connectedApiUrls.woland)) {
            apiName = ApiNamesEnum.Woland;
        }

        const errorCode = ErrorHelper.getErrorCode(error, apiName);
        return new ExtractedError(error, apiName, errorCode);
    }

    private async tryParseErrorBlob(errorResponse: HttpErrorResponse): Promise<ErrorDetails | null>  {
        const error = errorResponse.error;

        if ('type' in error && error['type'] === 'application/problem+json') {
            const errorText = await (error as Blob).text();
            return JSON.parse(errorText) as ErrorDetails;
        }

        return null;
    }
}

